import {
  ListaDeFichasSchema,
  ResultadoDeReconocimientoSchema,
  type AplicacionDeTipo,
  type EdicionDeFicha,
  type NuevaFicha,
  DocumentoDeCumplimientoSchema,
  EstadoDeCumplimientoSchema,
  HistorialDeCumplimientoSchema,
  ListaDeTiposDeCumplimientoSchema,
  ListaDeVinculosSchema,
  PaginaDeArchivoSchema,
  ResultadoDeArchivoDeCarpetaSchema,
  ResultadoDeRevisionDeCarpetaSchema,
  ResumenDeCumplimientoSchema,
  type CategoriaDeCumplimiento,
  type EdicionDeDocumentoDeCumplimiento,
  type EdicionDeTipoDeCumplimiento,
  type EdicionDeVinculo,
  type NuevoTipoDeCumplimiento,
  type NuevoVinculo,
} from '@cnc/contracts';
import {keepPreviousData, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {z} from 'zod';
import {json, pedir} from './cliente';

// Cumplimiento (decision 0045): resumen, situacion por categoria, historial,
// archivo, tipos, vinculos, carga, edicion, revision y carga de la carpeta.
export function useResumenDeCumplimiento(activo = true) {
  return useQuery({
    enabled: activo,
    queryKey: ['cumplimiento', 'resumen'],
    queryFn: () => pedir('cumplimiento/resumen', ResumenDeCumplimientoSchema),
    // Sirve al aviso del menu: se refresca solo cada cierto tiempo.
    refetchInterval: 10 * 60_000,
  });
}

export function useEstadoDeCumplimiento(categoria: CategoriaDeCumplimiento) {
  return useQuery({
    queryKey: ['cumplimiento', 'estado', categoria],
    queryFn: () => pedir(`cumplimiento/estado?categoria=${categoria}`, EstadoDeCumplimientoSchema),
  });
}

export function useHistorialDeCumplimiento(vinculoId: number, tipoId: number) {
  return useQuery({
    queryKey: ['cumplimiento', 'historial', vinculoId, tipoId],
    queryFn: () => pedir(`cumplimiento/historial?vinculo=${vinculoId}&tipo=${tipoId}`, HistorialDeCumplimientoSchema),
  });
}

export function useArchivoDeCumplimiento(cadena: string) {
  return useQuery({
    queryKey: ['cumplimiento', 'archivo', cadena],
    queryFn: () => pedir(`cumplimiento/archivo${cadena ? `?${cadena}` : ''}`, PaginaDeArchivoSchema),
    placeholderData: keepPreviousData,
  });
}

export function useTiposDeCumplimiento() {
  return useQuery({
    queryKey: ['cumplimiento', 'tipos'],
    queryFn: () => pedir('cumplimiento/tipos', ListaDeTiposDeCumplimientoSchema),
  });
}

export function useVinculosDeCumplimiento(categoria?: CategoriaDeCumplimiento) {
  return useQuery({
    queryKey: ['cumplimiento', 'vinculos', categoria ?? 'todas'],
    queryFn: () => pedir(`cumplimiento/vinculos${categoria ? `?categoria=${categoria}` : ''}`, ListaDeVinculosSchema),
  });
}

function useRefrescarCumplimiento() {
  const cliente = useQueryClient();
  return () => void cliente.invalidateQueries({queryKey: ['cumplimiento']});
}

export function useCrearTipoDeCumplimiento() {
  const refrescar = useRefrescarCumplimiento();
  return useMutation({
    mutationFn: (nuevo: NuevoTipoDeCumplimiento) =>
      pedir('cumplimiento/tipos', ListaDeTiposDeCumplimientoSchema, json('POST', nuevo)),
    onSuccess: refrescar,
  });
}

export function useAgregarTiposSugeridos() {
  const refrescar = useRefrescarCumplimiento();
  return useMutation({
    mutationFn: () => pedir('cumplimiento/tipos/sugeridos', ListaDeTiposDeCumplimientoSchema, {method: 'POST'}),
    onSuccess: refrescar,
  });
}

export function useEditarTipoDeCumplimiento() {
  const refrescar = useRefrescarCumplimiento();
  return useMutation({
    mutationFn: ({id, edicion}: {id: number; edicion: EdicionDeTipoDeCumplimiento}) =>
      pedir(`cumplimiento/tipos/${id}`, ListaDeTiposDeCumplimientoSchema, json('PATCH', edicion)),
    onSettled: refrescar,
  });
}

export function useCrearVinculo() {
  const refrescar = useRefrescarCumplimiento();
  return useMutation({
    mutationFn: (nuevo: NuevoVinculo) => pedir('cumplimiento/vinculos', ListaDeVinculosSchema, json('POST', nuevo)),
    onSuccess: refrescar,
  });
}

export function useEditarVinculo() {
  const refrescar = useRefrescarCumplimiento();
  return useMutation({
    mutationFn: ({id, edicion}: {id: number; edicion: EdicionDeVinculo}) =>
      pedir(`cumplimiento/vinculos/${id}`, ListaDeVinculosSchema, json('PATCH', edicion)),
    onSettled: refrescar,
  });
}

export type CargaDeDocumento = {
  vinculoId: number;
  tipoId: number;
  archivo: File | null;
  recibidoEl: string;
  expedidoEl: string;
  venceEl: string;
  referencia: string;
  notas: string;
  noAplica: boolean;
};

export function useCargarDocumentoDeCumplimiento() {
  const refrescar = useRefrescarCumplimiento();
  return useMutation({
    mutationFn: (c: CargaDeDocumento) => {
      const cuerpo = new FormData();
      if (c.archivo && !c.noAplica) cuerpo.append('archivo', c.archivo, c.archivo.name);
      cuerpo.append('vinculoId', String(c.vinculoId));
      cuerpo.append('tipoId', String(c.tipoId));
      if (!c.noAplica) {
        if (c.recibidoEl) cuerpo.append('recibidoEl', c.recibidoEl);
        if (c.expedidoEl) cuerpo.append('expedidoEl', c.expedidoEl);
        if (c.venceEl) cuerpo.append('venceEl', c.venceEl);
        if (c.referencia) cuerpo.append('referencia', c.referencia);
      }
      if (c.notas) cuerpo.append('notas', c.notas);
      cuerpo.append('noAplica', String(c.noAplica));
      return pedir('cumplimiento/documentos', HistorialDeCumplimientoSchema, {method: 'POST', body: cuerpo});
    },
    onSuccess: refrescar,
  });
}

export function useEditarDocumentoDeCumplimiento() {
  const refrescar = useRefrescarCumplimiento();
  return useMutation({
    mutationFn: ({id, edicion}: {id: number; edicion: EdicionDeDocumentoDeCumplimiento}) =>
      pedir(`cumplimiento/documentos/${id}`, DocumentoDeCumplimientoSchema, json('PATCH', edicion)),
    onSettled: refrescar,
  });
}

export function useRevisarDocumentoDeCumplimiento() {
  const refrescar = useRefrescarCumplimiento();
  return useMutation({
    mutationFn: ({
      id,
      decision,
      nota,
      version,
    }: {
      id: number;
      decision: 'aprobar' | 'rechazar';
      nota?: string;
      version: number;
    }) =>
      pedir(
        `cumplimiento/documentos/${id}/revision`,
        HistorialDeCumplimientoSchema,
        json('POST', {decision, nota, version}),
      ),
    onSettled: refrescar,
  });
}

export const enlaceDeArchivoDeCumplimiento = (id: number) => `/api/cumplimiento/documentos/${id}/archivo`;

// ── Carga de la carpeta de compliance ───────────────────────────────────────

// Ruta del archivo dentro de la carpeta elegida, sin la carpeta misma.
export const rutaDentroDeLaCarpeta = (archivo: File) =>
  (archivo.webkitRelativePath || archivo.name).split('/').slice(1).join('/') || archivo.name;

export function revisarCarpeta(archivos: File[]) {
  const lista = {
    archivos: archivos.map(a => ({
      ruta: rutaDentroDeLaCarpeta(a),
      tamanoBytes: a.size,
      modificado: new Date(a.lastModified).toISOString(),
    })),
  };
  const cuerpo = new FormData();
  cuerpo.append('lista', new Blob([JSON.stringify(lista)], {type: 'application/json'}), 'lista.json');
  return pedir('cumplimiento/carpeta/revisar', ResultadoDeRevisionDeCarpetaSchema, {method: 'POST', body: cuerpo});
}

export function subirArchivoDeCarpeta(archivo: File) {
  const cuerpo = new FormData();
  cuerpo.append('ruta', rutaDentroDeLaCarpeta(archivo));
  cuerpo.append('modificado', new Date(archivo.lastModified).toISOString());
  cuerpo.append('archivo', archivo, archivo.name);
  return pedir('cumplimiento/carpeta/archivo', ResultadoDeArchivoDeCarpetaSchema, {method: 'POST', body: cuerpo});
}

export function terminarCargaDeCarpeta(resumen: {
  cargados: number;
  yaEstaban: number;
  ignorados: number;
  errores: number;
}) {
  return pedir('cumplimiento/carpeta/terminar', z.object({ok: z.literal(true)}), json('POST', resumen));
}

// ── Vinculos: aplica o no, unir y volver a reconocer ────────────────────────

export function useAplicacionDeTipo() {
  const refrescar = useRefrescarCumplimiento();
  return useMutation({
    mutationFn: ({vinculoId, cambio}: {vinculoId: number; cambio: AplicacionDeTipo}) =>
      pedir(`cumplimiento/vinculos/${vinculoId}/aplicacion`, HistorialDeCumplimientoSchema, json('PUT', cambio)),
    onSettled: refrescar,
  });
}

export function useUnirVinculos() {
  const refrescar = useRefrescarCumplimiento();
  return useMutation({
    mutationFn: ({origenId, destinoId}: {origenId: number; destinoId: number}) =>
      pedir(`cumplimiento/vinculos/${origenId}/unir`, ListaDeVinculosSchema, json('POST', {destinoId})),
    onSettled: refrescar,
  });
}

export function useReconocerArchivos() {
  const refrescar = useRefrescarCumplimiento();
  return useMutation({
    mutationFn: () => pedir('cumplimiento/reconocer', ResultadoDeReconocimientoSchema, {method: 'POST'}),
    onSuccess: refrescar,
  });
}

// ── Fichas de empresas del grupo ────────────────────────────────────────────

export function useFichasDeEmpresas(habilitado: boolean) {
  return useQuery({
    queryKey: ['cumplimiento', 'fichas'],
    queryFn: () => pedir('cumplimiento/fichas', ListaDeFichasSchema),
    enabled: habilitado,
  });
}

export function useCrearFicha() {
  const refrescar = useRefrescarCumplimiento();
  return useMutation({
    mutationFn: (nueva: NuevaFicha) => pedir('cumplimiento/fichas', ListaDeFichasSchema, json('POST', nueva)),
    onSuccess: refrescar,
  });
}

export function useEditarFicha() {
  const refrescar = useRefrescarCumplimiento();
  return useMutation({
    mutationFn: ({id, edicion}: {id: number; edicion: EdicionDeFicha}) =>
      pedir(`cumplimiento/fichas/${id}`, ListaDeFichasSchema, json('PATCH', edicion)),
    onSettled: refrescar,
  });
}

export function useQuitarVinculo() {
  const refrescar = useRefrescarCumplimiento();
  return useMutation({
    mutationFn: (id: number) => pedir(`cumplimiento/vinculos/${id}`, ListaDeVinculosSchema, {method: 'DELETE'}),
    onSettled: refrescar,
  });
}
